import { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    direction: "up" | "down" | "neutral";
  };
  sparklineData?: number[];
  onClick?: () => void;
}

export function MetricCard({ title, value, icon: Icon, trend, sparklineData, onClick }: MetricCardProps) {
  const TrendIcon = trend?.direction === "up" ? TrendingUp : trend?.direction === "down" ? TrendingDown : Minus;
  const trendColor = trend?.direction === "up" ? "text-green-500" : trend?.direction === "down" ? "text-red-500" : "text-muted-foreground";

  return (
    <Card 
      className="group relative overflow-hidden backdrop-blur-xl bg-card/50 border-border/50 hover:bg-card/70 transition-all duration-300 cursor-pointer"
      onClick={onClick}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="p-2.5 rounded-xl bg-primary/10 backdrop-blur-sm flex-shrink-0">
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-xs sm:text-sm font-medium flex-shrink-0 ${trendColor}`}>
              <TrendIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>

        <div className="space-y-1 min-h-[4rem]">
          <p className="text-xs sm:text-sm text-muted-foreground font-medium line-clamp-2">{title}</p>
          <p className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{value}</p>
        </div>

        {sparklineData && sparklineData.length > 0 && (
          <div className="mt-3 h-8 flex items-end gap-0.5">
            {sparklineData.map((val, i) => (
              <div 
                key={i}
                className="flex-1 bg-gradient-to-t from-primary/40 to-primary/10 rounded-t-sm transition-all duration-300 group-hover:from-primary/60 group-hover:to-primary/20"
                style={{ height: `${(val / Math.max(...sparklineData)) * 100}%`, minHeight: '4px' }}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
